import React from "react";

import Button from "components/common/Button";
import Icon from "components/common/Icon";
import Modal from "components/common/Modal";
import TemplateCompletenessPanel from "components/documents/TemplateCompletenessPanel";

import useModal from "hooks/useModal";

export const CONFIRM_CREATE_WITH_MISSING_MODAL_NAME = "CONFIRM_CREATE_WITH_MISSING";

const ConfirmCreateWithMissingModal = ({
  name = CONFIRM_CREATE_WITH_MISSING_MODAL_NAME,
  onClose,
  onConfirm,
  clientId,
  summary,
  groups,
  wizardStats,
  wizardMissingSteps,
}) => {
  const { options = {}, close } = useModal(name);
  // Support triggering via global event so non-modal contexts can open it easily
  React.useEffect(() => {
    const handler = (e) => {
      if (!e?.detail?.name || e.detail.name !== name) {
        // The Modal component reads options from Redux; forwarding here allows children props fallback
      }
    };
    window.addEventListener("dp-open-modal", handler);
    return () => window.removeEventListener("dp-open-modal", handler);
  }, [name]);

  const effective = {
    ...options,
    onClose: onClose ?? options?.onClose,
    onConfirm: onConfirm ?? options?.onConfirm,
    // These two callbacks are only passed via `options`, but keep the explicit spread for clarity.
    onStartWizard: options?.onStartWizard,
    scrollToWizardPath: options?.scrollToWizardPath,

    clientId: clientId ?? options?.clientId,
    summary: summary ?? options?.summary,
    groups: groups ?? options?.groups,
    wizardStats: wizardStats ?? options?.wizardStats,
    wizardMissingSteps: wizardMissingSteps ?? options?.wizardMissingSteps,
  };
  return (
    <Modal
      name={name}
      size="lg"
      title="Incomplete Form"
      noFooter
      showCloseButton
      closeButtonTitle="×"
    >
      <div className="mb-3 text-sm text-gray-600">
        This form is not fully complete. Start the Form Wizard or create the form with missing
        information.
      </div>
      <TemplateCompletenessPanel
        clientId={effective.clientId}
        summary={effective.summary}
        groups={effective.groups}
        wizardStats={effective.wizardStats}
        wizardMissingSteps={effective.wizardMissingSteps}
        inModal
        onWizardOpenPath={(path) => {
          // Close modal, then trigger the in-page wizard scroll
          try {
            if (effective.onClose) effective.onClose();
          } catch {
            // Ignore close errors
          }
          // Use Redux toggle to close immediately without waiting for exit animation
          try {
            close();
          } catch {
            // Ignore close errors
          }
          setTimeout(() => {
            try {
              if (effective.onStartWizard) effective.onStartWizard();
              if (effective.scrollToWizardPath) {
                effective.scrollToWizardPath(path);
              } else {
                const event = new CustomEvent("dp-scroll-to-missing", { detail: { path } });
                window.dispatchEvent(event);
              }
            } catch {
              // Ignore wizard errors
            }
          }, 0);
        }}
      />
      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          className="btn-shimmer-always"
          onClick={() => {
            if (effective.onStartWizard) effective.onStartWizard();
            if (effective.onClose) effective.onClose();
            close();
            const el = document.getElementById("form-wizard");
            if (el && typeof el.scrollIntoView === "function")
              el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
        >
          <span className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <Icon name="check-circle" width={14} height={14} />
            <span>Start Form Wizard</span>
          </span>
        </Button>
        <Button
          className="btn-shimmer"
          onClick={() => {
            if (effective.onConfirm) effective.onConfirm();
            close();
          }}
        >
          Create
        </Button>
      </div>
    </Modal>
  );
};

export default ConfirmCreateWithMissingModal;
